import isApiError from "@insite/client-framework/Common/isApiError";
import { get, patch, post } from "@insite/client-framework/Services/ApiService";
import { BaseModel } from "@insite/client-framework/Types/ApiModels";
import { CreateQuoteLineParameter } from "@WhiteCap/Handlers/OsnQuotes/CreateAndAddQuoteLine";
import {
    CreateQuoteApiParameter,
    QuoteLineCollectionApiParameter,
} from "@WhiteCap/Handlers/OsnQuotes/CreateQuoteHandler";
import { UpdateQuoteApiParameter } from "@WhiteCap/Handlers/OsnQuotes/UpdateQuoteHandler";
import {
    CreateQuoteApiResultModel,
    GetQuoteApiResultModel,
    QuoteLineModel,
    UpdateQuoteApiResultModel,
} from "@WhiteCap/Store/Service/OsnQuotes/OsnQuotesApiModels";

const url = "/api/v1/quotes/osn";

// Used to get Create/Edit Quote Data
export async function getQuoteService(quoteNumber?: string) {
    try {
        const result = await get<GetQuoteApiResultModel>(`${url}?quote=${quoteNumber}`, {}, {}, "no-store");
        return {
            successful: true,
            result,
        };
    } catch (error) {
        if (isApiError(error)) {
            return {
                successful: false,
                errorMessage: error.errorJson.message || "Something went wrong!",
            };
        }
        throw error;
    }
}

// Used to submit data on Create Quote Page
export async function createQuoteService(
    branchContactId: string,
    createdBy: string,
    shipToId: string,
    billToId: string,
    warehouseId: string,
    shippingMethodCode: string,
    quoteName: string,
    quoteRequestedDate: string,
    poNumber: string,
    quoteExpiryDate: string,
    salesRepEmail: string,
    billToFirstName: string,
    billToLastName: string,
    shipToFirstName: string,
    shipToLastName: string,
    subtotal: number,
    quoteLineCollection: QuoteLineCollectionApiParameter[],
) {
    try {
        const result = await post<
            { quoteRequestModel: Omit<CreateQuoteApiParameter, "onError" | "onSuccess"> },
            CreateQuoteApiResultModel
        >(`${url}`, {
            quoteRequestModel: {
                branchContactId,
                createdBy,
                shipToId,
                billToId,
                warehouseId,
                shippingMethodCode,
                quoteName,
                quoteRequestedDate,
                poNumber,
                quoteExpiryDate,
                salesRepEmail,
                billToFirstName,
                billToLastName,
                shipToFirstName,
                shipToLastName,
                subtotal,
                quoteLineCollection,
            },
        });

        if (result.isSuccess) {
            return {
                successful: true,
                result,
            };
        }

        return {
            successful: false,
            errorMessage: "Something went wrong!",
        };
    } catch (error) {
        if (isApiError(error)) {
            return {
                successful: false,
                errorMessage: error.errorJson.message || "Something went wrong!",
            };
        }
        throw error;
    }
}

// Used to submit data on Edit Quote Page
export async function updateQuoteService(
    quoteNumber: string,
    createdBy: string,
    branchContactId: string,
    shipToId: string,
    billToId: string,
    warehouseId: string,
    shippingMethodCode: string,
    quoteName: string,
    quoteRequestedDate: string,
    poNumber: string,
    quoteExpiryDate: string,
    salesRepEmail: string,
    billToFirstName: string,
    billToLastName: string,
    shipToFirstName: string,
    shipToLastName: string,
    subtotal: number,
    quoteLineCollection: QuoteLineCollectionApiParameter[],
) {
    interface DataModel extends BaseModel {
        isSuccess: boolean;
        quoteRequestModel: Omit<UpdateQuoteApiParameter, "onError" | "onSuccess">;
        result: any;
    }
    try {
        const result = await patch<DataModel>(`${url}`, {
            quoteRequestModel: {
                quoteNumber,
                createdBy,
                branchContactId,
                shipToId,
                billToId,
                warehouseId,
                shippingMethodCode,
                quoteName,
                quoteRequestedDate,
                poNumber,
                quoteExpiryDate,
                salesRepEmail,
                billToFirstName,
                billToLastName,
                shipToFirstName,
                shipToLastName,
                subtotal,
                quoteLineCollection,
            },
        });
        if (result.isSuccess) {
            return {
                successful: true,
                result,
            };
        }
        return {
            successful: false,
            errorMessage: "Something went wrong!",
        };
    } catch (error) {
        if (isApiError(error)) {
            return {
                successful: false,
                errorMessage: error.errorJson.message || "Something went wrong!",
            };
        }
        throw error;
    }
}

export interface CreateQuoteLineResult {
    quoteLine?: QuoteLineModel;
    isSuccess: boolean;
    message?: string;
}

type CreateQuoteLineApiParamter = Omit<CreateQuoteLineParameter, "onSuccess" | "onError">;

export async function CreateQuoteLineService({
    productId,
    lineNumber,
    qty,
    billToId,
    shipToId,
    warehosueName,
}: CreateQuoteLineApiParamter): Promise<CreateQuoteLineResult> {
    try {
        const requestBody: CreateQuoteLineApiParamter = {
            productId,
            lineNumber,
            qty,
            billToId,
            shipToId,
            warehosueName,
        };

        const result = await post<CreateQuoteLineApiParamter, CreateQuoteLineResult>(
            `${url}/createQuoteLine`,
            requestBody
        );

        return {
            quoteLine: result.quoteLine,
            isSuccess: true,
        };
    } catch (error: any) {
        if (isApiError(error)) {
            return {
                isSuccess: false,
                message: error.errorJson?.message || "Failed to create quote line",
            };
        } if ("status" in error && error.status.toString().startsWith("4")) {
            return {
                isSuccess: false,
                message: error.errorMessage || "Something went wrong!",
            };
        }
        throw error;
    }
}
